import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Home, Inbox, Plus } from '@tamagui/lucide-icons'
import { useAppStore, useServerStore } from '@/store'
import { useToastStore } from '@/store/toastStore'
import { Badge, Tooltip } from '../common'
import { ServerPreview } from '../app/server/ServerPreview'
import { useState } from 'react'

const Rail = styled(View, {
  width: 64,
  backgroundColor: '$nav',
  alignItems: 'center',
  paddingVertical: 8,
  borderRightWidth: 1,
  borderRightColor: '$borderDefault',
  flexShrink: 0,
})

const ServerIconWrapper = styled(View, {
  width: 48,
  height: 48,
  marginVertical: 4,
  position: 'relative',
  cursor: 'pointer',
})

const ServerIconOuter = styled(View, {
  width: 48,
  height: 48,
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  // @ts-expect-error -- web transition
  style: { transition: 'border-radius 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms ease' },
})

const ServerInitials = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 16,
  color: '$surfaceForeground',
})

const ActiveIndicator = styled(View, {
  position: 'absolute',
  left: -8,
  width: 4,
  backgroundColor: '$accent',
  borderTopRightRadius: '$borderRadiusSm',
  borderBottomRightRadius: '$borderRadiusSm',
  // @ts-expect-error -- web transition
  style: { transition: 'height 150ms ease' },
})

const Separator = styled(View, {
  width: 32,
  height: 2,
  backgroundColor: '$borderDefault',
  borderRadius: '$borderRadiusSm',
  marginVertical: 6,
})

const HomeButton = styled(View, {
  width: 48,
  height: 48,
  borderRadius: '$borderRadius2xl',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$sidebar',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$accent',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, border-radius 150ms ease' },
})

const InboxButton = styled(View, {
  width: 48,
  height: 48,
  borderRadius: '$borderRadius2xl',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$sidebar',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const CreateButton = styled(View, {
  width: 48,
  height: 48,
  borderRadius: '$borderRadius2xl',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$sidebar',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$success',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, border-radius 150ms ease' },
})

interface ServerIconProps {
  serverId: string
  onDragStart: (e: React.DragEvent, id: string) => void
  onDragOver: (e: React.DragEvent, id: string) => void
  onDrop: (e: React.DragEvent, id: string) => void
  onDragEnd: () => void
  dragOverId: string | null
  dragPosition: 'above' | 'below' | null
  isDragging: string | null
}

function ServerIcon({ serverId, onDragStart, onDragOver, onDrop, onDragEnd, dragOverId, dragPosition, isDragging }: ServerIconProps) {
  const server = useServerStore((s) => s.servers[serverId])
  const { activeServerId, currentView, mutedServerIds, navigateToServer, setActiveChannel, openContextMenu } = useAppStore()
  const isActive = currentView === 'server' && activeServerId === serverId
  const isMuted = mutedServerIds.includes(serverId)
  const [hovered, setHovered] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const isBeingDragged = isDragging === serverId

  if (!server) return null

  const handleClick = () => {
    navigateToServer(serverId)
    const firstTextChannel = server.channels.find(c => c.type === 'text')
    if (firstTextChannel) {
      setActiveChannel(firstTextChannel.id)
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    openContextMenu('server', serverId, e.clientX, e.clientY)
  }

  return (
    <ServerIconWrapper
      onMouseEnter={() => { setHovered(true); setShowPreview(true) }}
      onMouseLeave={() => { setHovered(false); setShowPreview(false) }}
      onPress={handleClick}
      // @ts-expect-error -- web event
      onContextMenu={handleContextMenu}
      draggable
      onDragStart={(e: any) => onDragStart(e, serverId)}
      onDragOver={(e: any) => onDragOver(e, serverId)}
      onDrop={(e: any) => onDrop(e, serverId)}
      onDragEnd={onDragEnd}
      opacity={isBeingDragged ? 0.4 : 1}
    >
      {/* Drop indicator */}
      {dragOverId === serverId && dragPosition === 'above' && (
        <View position="absolute" top={-2} left={8} right={8} height={2} backgroundColor="$accent" borderRadius="$borderRadiusSm" />
      )}
      {dragOverId === serverId && dragPosition === 'below' && (
        <View position="absolute" bottom={-2} left={8} right={8} height={2} backgroundColor="$accent" borderRadius="$borderRadiusSm" />
      )}

      {(isActive || hovered) && (
        <ActiveIndicator
          height={isActive ? 36 : 20}
          top={isActive ? 6 : 14}
        />
      )}

      <ServerIconOuter
        backgroundColor={isActive ? server.iconColor : '$sidebar'}
        borderRadius={isActive || hovered ? '$borderRadiusXl' : '$borderRadius3xl'}
        {...(isActive && {
          // @ts-expect-error -- web box-shadow
          style: {
            boxShadow: `0 0 12px ${server.iconColor}33`,
            transition: 'border-radius 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms ease',
          },
        })}
        {...(!isActive && hovered && {
          backgroundColor: '$sidebarHover',
        })}
      >
        <ServerInitials
          color={isActive ? '$surface' : '$surfaceForeground'}
        >
          {server.iconText}
        </ServerInitials>
      </ServerIconOuter>

      {/* Muted overlay */}
      {isMuted && !isActive && (
        <View
          position="absolute"
          bottom={-2}
          left={-2}
          width={16}
          height={16}
          borderRadius="$borderRadiusLg"
          backgroundColor="$surface"
          alignItems="center"
          justifyContent="center"
        >
          <Text fontSize={9} color="$sidebarMuted">🔕</Text>
        </View>
      )}

      {/* Unread dot — skip if muted */}
      {!isActive && !isMuted && server.unreadCount > 0 && !server.mentionCount && (
        <View
          position="absolute"
          left={-4}
          top={20}
          width={8}
          height={8}
          borderRadius="$borderRadiusSm"
          backgroundColor="$surfaceForeground"
        />
      )}

      {/* Mention badge */}
      {server.mentionCount > 0 && (
        <View position="absolute" bottom={-2} right={-2}>
          <Badge count={server.mentionCount} variant="mention" />
        </View>
      )}

      {/* Server preview tooltip */}
      {showPreview && !isActive && (
        <View
          position="absolute"
          left={56}
          top={-20}
          zIndex={1000}
        >
          <ServerPreview serverId={serverId} />
        </View>
      )}
    </ServerIconWrapper>
  )
}

export function ServerRail() {
  const { currentView, toggleInbox, navigateHome, openCreateServerModal } = useAppStore()
  const addToast = useToastStore((s) => s.addToast)
  const serverOrder = useServerStore((s) => s.serverOrder)
  const reorderServers = useServerStore((s) => s.reorderServers)
  const theme = useTheme()
  const isHomeActive = currentView === 'home' || currentView === 'dm'
  const btnShadow = theme.shadowButton?.val
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [dragPosition, setDragPosition] = useState<'above' | 'below' | null>(null)
  const [isDragging, setIsDragging] = useState<string | null>(null)

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('text/plain', id)
    e.dataTransfer.effectAllowed = 'move'
    setIsDragging(id)
  }

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    setDragOverId(id)
    setDragPosition(e.clientY < midY ? 'above' : 'below')
  }

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    const draggedId = e.dataTransfer.getData('text/plain')
    if (!draggedId || draggedId === targetId) {
      setDragOverId(null)
      setDragPosition(null)
      setIsDragging(null)
      return
    }
    const newOrder = serverOrder.filter((id) => id !== draggedId)
    const targetIndex = newOrder.indexOf(targetId)
    const insertIndex = dragPosition === 'below' ? targetIndex + 1 : targetIndex
    newOrder.splice(insertIndex, 0, draggedId)
    reorderServers(newOrder)
    addToast('success', 'Server order updated')
    setDragOverId(null)
    setDragPosition(null)
    setIsDragging(null)
  }

  const handleDragEnd = () => {
    setDragOverId(null)
    setDragPosition(null)
    setIsDragging(null)
  }

  return (
    <Rail>
      {/* Home / DM button */}
      <HomeButton
        onPress={navigateHome}
        {...(isHomeActive && {
          backgroundColor: '$accent',
          borderRadius: '$borderRadiusXl',
        })}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: isHomeActive ? btnShadow : undefined }}
      >
        <Home size={20} color={isHomeActive ? '$surface' : '$surfaceForeground'} />
      </HomeButton>

      <Separator />

      {/* Inbox button */}
      <View position="relative">
        <InboxButton onPress={toggleInbox}>
          <Inbox size={18} color="$sidebarMuted" />
        </InboxButton>
        {(() => {
          const servers = useServerStore.getState().servers
          const totalMentions = Object.values(servers).reduce((sum, s) => sum + s.mentionCount, 0)
          if (totalMentions <= 0) return null
          return (
            <View position="absolute" top={-2} right={-2}>
              <Badge count={totalMentions} variant="mention" />
            </View>
          )
        })()}
      </View>

      <Separator />

      {/* Server list */}
      <ScrollView
        flex={1}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
      >
        {serverOrder.map((id) => (
          <ServerIcon
            key={id}
            serverId={id}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            dragOverId={dragOverId}
            dragPosition={dragPosition}
            isDragging={isDragging}
          />
        ))}
      </ScrollView>

      <Separator />

      {/* Create server button */}
      <CreateButton
        onPress={openCreateServerModal}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: btnShadow }}
      >
        <Plus size={22} color="$success" />
      </CreateButton>
    </Rail>
  )
}
